// src/pages/ROICalculator/DataSourcesModal.jsx

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const DataSourcesModal = ({ open, onClose, sources }) => {
  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Data Sources
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            All research and statistics used in our ROI calculations
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ ml: 2 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 3 }}>
        {/* Introduction */}
        <Box
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#e3f2fd',
            borderRadius: 2,
            border: '1px solid #90caf9'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Our Commitment to Transparency:</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Every calculation in our ROI calculator is based on peer-reviewed research,
            authoritative business sources, and government statistics. We believe in
            complete transparency, which is why we provide full citations for all data
            used. Click any source below to verify the information yourself.
          </Typography>
        </Box>

        {/* Source Categories */}
        {sources.map((category, categoryIndex) => (
          <Accordion
            key={categoryIndex}
            expanded={expanded === `panel${categoryIndex}`}
            onChange={handleAccordionChange(`panel${categoryIndex}`)}
            sx={{
              mb: 2,
              borderRadius: 2,
              '&:before': { display: 'none' },
              boxShadow: 1,
              '&.Mui-expanded': {
                boxShadow: 3
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: '#f5f7fa',
                borderRadius: expanded === `panel${categoryIndex}` ? '8px 8px 0 0' : 2,
                '&:hover': {
                  bgcolor: '#e3f2fd'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckIcon color="success" />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {category.category}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {category.sources.length} source{category.sources.length > 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 3 }}>
              {category.sources.map((source, sourceIndex) => (
                <Box
                  key={sourceIndex}
                  sx={{
                    mb: sourceIndex < category.sources.length - 1 ? 3 : 0,
                    pb: sourceIndex < category.sources.length - 1 ? 3 : 0,
                    borderBottom:
                      sourceIndex < category.sources.length - 1
                        ? '1px solid'
                        : 'none',
                    borderColor: 'divider'
                  }}
                >
                  {/* Source Name and Link */}
                  <Box sx={{ mb: 1.5 }}>
                    <Link
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textDecoration: 'none',
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {source.name}
                      <OpenInNewIcon fontSize="small" />
                    </Link>
                  </Box>

                  {/* Key Statistics */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 1, fontWeight: 500 }}
                  >
                    Key Statistics Used:
                  </Typography>
                  <List dense sx={{ pl: 2 }}>
                    {source.stats.map((stat, statIndex) => (
                      <ListItem key={statIndex} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              • {stat}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Verification Note */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: '#fff3cd',
            borderRadius: 2,
            border: '1px solid #ffc107'
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>✓ Verified Research</strong>
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            All sources have been reviewed for credibility and accuracy. We regularly
            update our data as new research becomes available. Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
          </Typography>
        </Box>

       
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DataSourcesModal;